/**
 * Unit Prices (IP-61, IP-111, IP-112; design 2026-10-02 sections 6 to 8),
 * the screen's logic without React, so node --test reads it directly.
 *
 * A price file uploaded on File Uploads - NAV values - is accepted by the
 * system once its scan passes, and builds one NAV run per fund in it
 * (pricing/assemble.js). Each run carries its checks
 * (pricing/checks.js) and moves CHECKED -> PUBLISHED on one approval, or is
 * QUARANTINED by a failing check, REJECTED, or SUPERSEDED by a later version.
 * Three decisions move it, each on a fresh authenticator code (D-D, IP-111):
 *
 *   PRICE_OVERRIDE  core.price.override: DAY_ON_DAY alone, FAILing on a
 *                   QUARANTINED run not overridden yet, with a reason of 20
 *                   or more characters (actions.js assertOverridable)
 *   PRICE_APPROVE   core.price.approve: a CHECKED run, by neither its maker
 *                   (the uploader) nor its overrider (assertApprover) -
 *                   publishes the price (no Trustee leg, migration 0016)
 *   PRICE_REJECT    core.price.approve: any live run (CHECKED, QUARANTINED,
 *                   and APPROVED - a run approved before 0016, which waits
 *                   for a Trustee leg that is gone), with a reason
 *
 * The page offers a button only where the server would take the request
 * (runOffers): the claim (SCREEN_RULES.unitPrices, held to the catalogue by
 * auth/screenGates.registry.test.js), the run's state, and - said as visible
 * text, never only a disabled button - the four-eyes rule. The server still
 * judges every request on its own facts.
 *
 * Each code is bound to the action's params as the catalogue reads them
 * (actionOf(type).params), so the params are built here with that same
 * function: the figures are the run's own as the person read them, and a run
 * whose figures moved since is refused (PAYLOAD_MISMATCH), never approved at
 * other numbers. The route takes navRunId from its path, so the body is the
 * params without it (bodyOf).
 *
 * The tab's badge (awaitingViewer) counts the runs waiting for THIS person:
 * the CHECKED runs they may approve, if they hold core.price.approve - their
 * own runs left out, since four eyes would refuse them - with "+" when there
 * are more runs than one page.
 *
 * Every figure arrives as a decimal string and is shown as it is.
 */
import { actionOf } from '@lfoc/domain/authority/catalogue.js';

export const RUN_STATES = Object.freeze(['CHECKED', 'QUARANTINED', 'APPROVED', 'PUBLISHED', 'REJECTED', 'SUPERSEDED']);

/** The states a run can still be rejected in (pricing/sql.js LIVE_STATES). */
export const LIVE_STATES = Object.freeze(['CHECKED', 'QUARANTINED', 'APPROVED']);

export const STATE_COLOUR = Object.freeze({
    CHECKED: 'blue', QUARANTINED: 'red', APPROVED: 'gold', PUBLISHED: 'green', REJECTED: 'default', SUPERSEDED: 'default'
});

/** What a run's state asks for next, in words. */
export const STATE_HINT = Object.freeze({
    CHECKED: 'Waiting for approval',
    QUARANTINED: 'A check failed: override it or reject the run',
    APPROVED: 'Approved before the Trustee step was removed: reject it and upload the file again',
    PUBLISHED: 'Published',
    REJECTED: 'Rejected',
    SUPERSEDED: 'Superseded by a later version'
});

export const OUTCOME_COLOUR = Object.freeze({ PASS: 'green', WARN: 'gold', FAIL: 'red', NOT_APPLICABLE: 'default', OVERRIDDEN: 'purple' });

/** pricing/checks.js CHECK, in words. */
export const CHECK_LABEL = Object.freeze({
    NEGATIVE_NAV: 'NAV above zero',
    ZERO_UNITS: 'Units in issue',
    UNITS_PROJECTION: 'Register agrees with its ledger',
    CROSS_FOOT: 'NAV per unit x units = NAV',
    DAY_ON_DAY: 'Day-on-day move',
    STALE_CHAIN: 'Price on the previous business day',
    UNITS_CROSSCHECK: 'File\'s units against the register',
    EXIT_FEE_PRICE: 'Exit-fee price',
    CHARGE_BASE_ABOVE_NAV: 'Charge base within the NAV',
    // IP-195: transactions dated before the run's date still to post.
    UNITS_PENDING: 'Earlier transactions posted'
});

/** price.source (0012 adds IMPORT, INITIAL price history - never a dealing price, IP-105). */
export const SOURCE_LABEL = Object.freeze({ FILE: 'NAV run', ATRAD: 'ATRAD', DEV_SEED: 'Dev seed', IMPORT: 'Imported history' });

/** The only check Compliance may override (pricing/checks.js OVERRIDABLE_CHECKS). */
export const OVERRIDABLE_CHECK = 'DAY_ON_DAY';

/** nav_run_override_reason_length, and PRICE_OVERRIDE's params. */
export const OVERRIDE_REASON_MIN = 20;

/** How many runs of a state the tab's badge reads (pricing/reads.js MAX_PAGE is 200). */
export const BADGE_PAGE = 50;

/** A page of runs or prices on the screen. */
export const PAGE = 50;

const same = (a, b) => typeof a === 'string' && typeof b === 'string' && a.toLowerCase() === b.toLowerCase();

/**
 * A person a read names, by the name the API gives next to the uid (design
 * section 6: <role>Name), else the uid, else null.
 *
 * @param {object} record a run, a batch, a trail entry
 * @param {string} uidKey e.g. 'makerUid', 'approvedBy'
 */
export function personOf(record, uidKey) {
    if (!record) return null;
    const nameKey = uidKey.endsWith('Uid') ? uidKey.slice(0, -3) + 'Name' : uidKey + 'Name';
    return record[nameKey] ?? record[uidKey] ?? null;
}

/**
 * The current price cards: one per ACTIVE fund (GET register/funds), with its
 * latest published price - the four prices, the dealing date and the source
 * - or null when it has none yet.
 */
export function priceCards(funds) {
    return (funds ?? [])
        .filter((f) => f.status === 'ACTIVE')
        .map((f) => ({
            fundId: f.fundId,
            name: f.name ?? f.shortName ?? f.fundId,
            price: f.price
                ? {
                    navPerUnit: f.price.navPerUnit ?? null,
                    sellingPrice: f.price.sellingPrice ?? null,
                    buyingPrice: f.price.buyingPrice ?? null,
                    buyingPriceWithExitFee: f.price.buyingPriceWithExitFee ?? null,
                    sellingPriceWithEntryFee: f.price.sellingPriceWithEntryFee ?? null,
                    dealingDate: f.price.dealingDate ?? null,
                    source: f.price.source ?? null
                }
                : null
        }));
}

/** A run's checks that are not a plain PASS (the table's badges), in the run's own order. */
export function checkBadges(checks) {
    return (checks ?? [])
        .filter((c) => c.outcome !== 'PASS' && c.outcome !== 'NOT_APPLICABLE')
        .map((c) => ({ code: c.code, outcome: c.outcome, colour: OUTCOME_COLOUR[c.outcome] ?? 'default' }));
}

/**
 * A run's checks as the drawer's Checks table shows them. An overridden check
 * carries only its overrider's uid (actions.js: override { by, reason, at,
 * evidenceId, was }); the name is the run's own overrideByName (IP-112, null
 * to an externally-safe reader), so it is set beside the uid when the check's
 * overrider is the run's (CON-5: the table read an override.byName nobody
 * sends, and named the person by uid).
 */
export function checkRows(run) {
    return (run?.checks ?? []).map((c) => (c.override
        ? { ...c, override: { ...c.override, byName: same(c.override.by, run.overrideBy) ? run.overrideByName ?? null : null } }
        : c));
}

/** The DAY_ON_DAY check a PRICE_OVERRIDE could act on, or null (actions.js assertOverridable, read-only). */
export function overridableCheck(run) {
    if (!run || run.state !== 'QUARANTINED' || run.overrideBy) return null;
    return (run.checks ?? []).find((c) => c.code === OVERRIDABLE_CHECK && c.outcome === 'FAIL') ?? null;
}

/**
 * IP-195: a run held because its fund still has transactions dated before its date to post -
 * carried over at day end, reconciled but not approved. The price is shown, never approvable:
 * the transactions are decided first, then the NAV file uploaded again. Answers the check (its
 * detail names each transaction, its pending list one per line), or null.
 */
export function unitsPendingCheck(run) {
    return (run?.checks ?? []).find((c) => c.code === 'UNITS_PENDING' && c.outcome === 'FAIL') ?? null;
}

/** Why this person may not approve the run (four eyes), or null. */
export function approveBlocked(run, uid) {
    if (same(uid, run?.makerUid)) return 'You uploaded the file this run came from, so another person must approve it.';
    if (same(uid, run?.overrideBy)) return 'You overrode its check, so another person must approve it.';
    return null;
}

/**
 * The three decisions as this person may take them on this run: each is
 * offered (the button shows) when they hold its claim and the run's state
 * admits it, and blocked - the button disabled, the reason said as text -
 * when four eyes stands in the way.
 *
 * @param {object} run a mapRun row
 * @param {{ uid: string, canOverride: boolean, canApprove: boolean, canReject: boolean }} who
 * @returns {Record<'override'|'approve'|'reject', { offered: boolean, blocked: string|null }>}
 */
export function runOffers(run, { uid, canOverride, canApprove, canReject }) {
    const state = run?.state;
    return {
        override: { offered: Boolean(canOverride && overridableCheck(run)), blocked: null },
        approve: { offered: Boolean(canApprove && state === 'CHECKED'), blocked: approveBlocked(run, uid) },
        reject: { offered: Boolean(canReject && LIVE_STATES.includes(state)), blocked: null }
    };
}

/**
 * The runs waiting for this person (the Unit Prices tab's badge).
 *
 * @param {{ checked: object[], moreChecked?: boolean }} page one page of CHECKED runs
 * @param {{ uid: string, canApprove: boolean }} who
 * @returns {{ count: number, more: boolean }}
 */
export function awaitingViewer({ checked = [], moreChecked = false }, { uid, canApprove }) {
    const toApprove = canApprove ? checked.filter((r) => r.state === 'CHECKED' && !approveBlocked(r, uid)).length : 0;
    return { count: toApprove, more: Boolean(canApprove && moreChecked) };
}

/** The badge's text: the count, with "+" when there are more than a page holds; null for nothing to show. */
export function badgeText({ count, more }) {
    if (!count && !more) return null;
    return String(count) + (more ? '+' : '');
}

/** A catalogue refusal of params as the form shows it: the message after "field: ", and the field. */
function validationOf(error) {
    if (error?.code !== 'VALIDATION') throw error;
    const field = error.details?.field ?? null;
    const message = field && error.message.startsWith(field + ': ') ? error.message.slice(field.length + 2) : error.message;
    // A reason is the person's to fix, in the form; a figure is the run's own, so it is named.
    const said = field && field !== 'reason' ? field + ' ' + message : message.charAt(0).toUpperCase() + message.slice(1);
    return { error: said + '.', field };
}

function paramsOf(type, input) {
    try {
        return { params: actionOf(type).params(input) };
    } catch (error) {
        return validationOf(error);
    }
}

/**
 * PRICE_APPROVE's params: the five figures to be published, as the person read them (no exit-fee price is null;
 * no selling price with the front-end fee - a run struck before migration 0034 - is null).
 */
export const approveParams = (run) => paramsOf('PRICE_APPROVE', {
    navRunId: run.navRunId,
    navPerUnit: run.navPerUnit,
    sellingPrice: run.sellingPrice,
    buyingPrice: run.buyingPrice,
    buyingPriceWithExitFee: run.buyingPriceWithExitFee ?? null,
    sellingPriceWithEntryFee: run.sellingPriceWithEntryFee ?? null
});

/** PRICE_OVERRIDE's params: DAY_ON_DAY, with a reason of 20 or more characters. */
export const overrideParams = (run, reason) => paramsOf('PRICE_OVERRIDE', { navRunId: run.navRunId, checkCode: OVERRIDABLE_CHECK, reason: reason ?? '' });

/** PRICE_REJECT's params: the reason. */
export const rejectParams = (run, reason) => paramsOf('PRICE_REJECT', { navRunId: run.navRunId, reason: reason ?? '' });

/** The body a decision posts: its params less navRunId, which the route reads from its path. */
export function bodyOf(params) {
    const { navRunId: _inPath, ...body } = params;
    return body;
}

/**
 * A refused decision that says the page was out of date (404, or 409 - the
 * run moved, its figures changed, someone else decided first): the open
 * dialog closes, the server's words show on the drawer, and the run and the
 * lists are read again (as Settings > Fund config does).
 */
export function isStale(error) {
    return error?.status === 404 || error?.status === 409;
}

/** A list query without the filters left empty (endpoints.js queryString drops them too). */
function compact(query) {
    return Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== ''));
}

/**
 * GET pricing/runs's query from the filters: fund, state, a valuation-date
 * range (YYYY-MM-DD strings), and batch - a batch id, or 'latest' for the
 * file the newest runs came from.
 */
export function runsQuery({ fundId = null, state = null, from = null, to = null, batch = null, cursor = null } = {}) {
    return compact({ fundId, state, from, to, batch, limit: PAGE, cursor });
}

/**
 * The runs of the file the newest run came from (the latest createdAt), from
 * a list that holds every file's: what ?batch=latest answers, for an API that
 * does not know it yet. A file's runs are made together, so they share it.
 */
export function latestFileRuns(runs) {
    const newest = (runs ?? []).reduce((best, r) => (!best || Date.parse(r.createdAt) > Date.parse(best.createdAt) ? r : best), null);
    return newest ? runs.filter((r) => r.batchId === newest.batchId) : [];
}

/** GET pricing/prices's query: fund, a dealing-date range and whether superseded versions show. */
export function pricesQuery({ fundId = null, from = null, to = null, includeSuperseded = false, cursor = null } = {}) {
    return compact({ fundId, from, to, includeSuperseded: includeSuperseded ? 'true' : null, limit: PAGE, cursor });
}

/** GET pricing/prices/export's query: the price history's filters, with no page. */
export function pricesExportQuery({ fundId = null, from = null, to = null, includeSuperseded = false } = {}) {
    return compact({ fundId, from, to, includeSuperseded: includeSuperseded ? 'true' : null });
}

/** A page appended to what is shown, de-duplicated on its key (a run or price that moved between pages shows once). */
export function appendPage(shown, page, key) {
    const seen = new Set(shown.map((row) => row[key]));
    return [...shown, ...page.filter((row) => !seen.has(row[key]))];
}

/** A trail entry's action in words. */
export const TRAIL_LABEL = Object.freeze({
    PRICE_OVERRIDE: 'Check overridden',
    PRICE_APPROVE: 'Approved and published',
    PRICE_REJECT: 'Rejected',
    // A run verified before migration 0016 keeps its Trustee leg in its trail.
    TRUSTEE_VERIFY: 'Verified by the Trustee and published',
    INTAKE_ACCEPT: 'File accepted'
});

/** The trail as the drawer lists it: when, who (by name), what, and the details in a line. */
export function trailRows(trail) {
    return (trail ?? []).map((a) => ({
        key: String(a.auditId ?? a.occurredAt + a.actionType),
        occurredAt: a.occurredAt ?? null,
        who: personOf(a, 'actorUid') ?? '—',
        action: TRAIL_LABEL[a.actionType] ?? a.actionType,
        details: detailLine(a.details)
    }));
}

function detailLine(details) {
    if (!details || typeof details !== 'object') return '';
    return Object.entries(details)
        .filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object')
        .map(([k, v]) => k + ': ' + String(v))
        .join(' · ');
}

/** The day-on-day move as a percentage, or a dash. */
export function dayOnDay(run) {
    return run?.dayOnDayPct === null || run?.dayOnDayPct === undefined ? '—' : run.dayOnDayPct + ' %';
}
